import { FC, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Descriptions, Divider, Flex } from "antd";
import dayjs from "dayjs";
import { ColumnsType } from "antd/lib/table";
import { AnyObject } from "antd/lib/_util/type";

import { SaleItemState, SaleOrder, SaleOrderItemState } from "@/types/sales";
import STableLayout from "@/styles/STableLayout";
import SText from "@/styles/SText";
import { CreditLevelSelect, CurrencySelect, CountrySelect, DepartmentSelect } from "@/components/Selector";
import Dict from "@/components/Dict";
import { DictEnum } from "@/types/dict";
import { dateFormat, priceFormat } from "@/utils/format";

interface SaleDetailProps {
  saleOrder: SaleOrderItemState | null;
}

const SaleDetail: FC<SaleDetailProps> = ({ saleOrder }) => {
  const { t } = useTranslation();

  // 订单主体信息 Descriptions
  const orderDescriptions = useMemo(() => [
    {
      key: "number",
      label: t("sale.orderNumber"),
      children: <SText text={saleOrder?.number || ""} />,
    },
    {
      key: "innerNumber",
      label: t("sale.innerNumber"),
      children: <SText text={saleOrder?.innerNumber || ""} />,
    },
    {
      key: "shortName",
      label: t("sale.shortName"),
      children: <SText text={saleOrder?.shortName || ""} />,
    },
    {
      key: "currency",
      label: t("sale.currency"),
      children: <CurrencySelect value={saleOrder?.currency} readOnly />,
    },
    {
      key: "taxRate",
      label: t("sale.taxRate"),
      children: <SText text={saleOrder?.taxRate || ""} suffix="%" />,
    },

  ], [saleOrder, t]);

  // 订单明细表格列
  const itemColumns: ColumnsType<SaleItemState> = useMemo(() => [
    {
      title: t("product.code"),
      dataIndex: ["product", "code"],
      key: "productCode",
      width: 120,
      render: (_, item) => <SText text={item?.product?.code ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.name"),
      dataIndex: ["product", "name"],
      key: "productName",
      width: 150,
      render: (_, item) => <SText text={item?.product?.name ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.model"),
      dataIndex: ["product", "model"],
      key: "productModel",
      width: 120,
      render: (_, item) => <SText text={item?.product?.model ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("product.uom"),
      dataIndex: ["product", "uom"],
      key: "uom",
      width: 100,
      render: (_, item) => <SText text={item?.product?.uom?.toUpperCase() ?? ""} style={{ width: "100%" }} />,
    },
    {
      title: t("sale.quantity"),
      dataIndex: "quantity",
      key: "quantity",
      width: 100,
      render: (_, item) => <SText text={(item?.quantity ?? 0) + ''} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.taxPrice")} />,
      dataIndex: "price",
      width: 120,
      render: (_, item) => <SText text={priceFormat(item?.price ?? '')} style={{ width: "100%" }} />,
    },
    {
      title: <SText text={t("sale.currency")} />,
      dataIndex: "currency",
      width: 100,
      render: (val, item) => (
        <CurrencySelect key={item?.customer?.id} value={saleOrder?.currency || ""} readOnly={true} />
      ),
    },
    {
      title: <SText text={t("purchase.rateAmount")} />,
      dataIndex: "totalMount",
      width: 140,
      render: (_, item) => (
        <SText text={item?.realAmount ?? ""} style={{ width: "100%" }} />
      ),
    },
    {
      title: <SText text={t("purchase.amount")} />,
      dataIndex: "totalMount",
      width: 140,
      render: (_, item) => (
        <SText text={item?.totalRealAmount ?? ""} style={{ width: "100%" }} />
      ),
    },
  ], [t]);

  return (
    <Flex vertical style={{ width: "100%", height: "100%" }}>
      {/* 订单主体信息 */}
      <Descriptions title={t("sale.orderInfo")} items={orderDescriptions} column={3} size="small" />

      <Divider />

      {/* 订单明细表格 */}
      <STableLayout
        dataSource={(saleOrder?.items || [])}
        columns={itemColumns as Array<AnyObject>}
        scrollX="max-content"
        headerChildren={null}
        headerSub={null}
        total={saleOrder?.items?.length || 0}
        handleChange={() => { }}
        pagination={false}
        extHeight={70}
      />
    </Flex>
  );
};

export default SaleDetail;
